import { TmapApp } from '@tmap-web-lib/tmap-app-interface';
import { TmapLog } from '@tmap-web-lib/tmap-log-client';
import { getSearchParams } from '@tmap-web-lib/utils';
import { isAxiosError as isAxiosError$ } from 'axios';
import { CSSProperties, useEffect, useRef } from 'react';
import { type FallbackProps } from 'react-error-boundary';

import { APP_ENV } from '../../constants/app-env';
import { formatError2ErrorInfo } from '../../utils/error';

type Props = FallbackProps;

const ERROR_DISPLAY_MESSAGE = '오류가 발생했어요. 다시 시도해 주세요.';

/**
 * TmapLog가 초기화되지 않은 상태에서도 에러 로그를 전송할 수 있도록
 * 최소한의 정보로 init 후 에러 이벤트를 송출합니다.
 */
function sendErrorExposeLog(error: Error) {
  const isPrd = APP_ENV.TARGET === 'prd';

  const searchParams = getSearchParams<{
    deviceid?: string;
    carrierName?: string;
    euk?: string;
    sessionid?: string;
  }>();

  TmapLog.init({
    serviceId: 'vertical_bridge',
    appVersion: TmapApp.env.isInApp ? TmapApp.env.appVersion : '10.15.0',
    carrier: searchParams.carrierName ?? 'ETC',
    deviceId: searchParams.deviceid ?? '',
    sessionId: searchParams.sessionid ?? '',
    mixpanel: {
      api_host: APP_ENV.LOG_COLLECTOR_URL,
      api_payload_format: 'base64',
      api_method: 'POST',
      token: APP_ENV.LOG_COLLECTOR_TOKEN,
      identity: searchParams.euk ?? '',
      debug: !isPrd,
    },
    onSend: (tmapLogBuilder) => {
      tmapLogBuilder
        .setPageType('WEB')
        .setCustom('deploy_env', APP_ENV.TARGET)
        .setEuk(searchParams.euk ?? '');
    },
  });

  const log = TmapLog('expose').setServiceId('error').setPageId('/error/error_exposed');

  // API 에러 정보 추출 (AxiosError인 경우)
  let apiPath = '';
  let statusCode = '';

  const isAxiosError = isAxiosError$(error);

  if (isAxiosError) {
    if (error.config?.url) {
      apiPath = error.config.url;
    }
    if (error.response?.status) {
      statusCode = String(error.response.status);
    }
  }

  const custom: Record<string, string> = {
    display_message: ERROR_DISPLAY_MESSAGE,
    display_type: 'full_screen',
    platform: 'inapp_web',
    service_name: 'vertical_bridge',
    screen_name: '/vertical_bridge/error',
    error_message: error.message || '',
    error_code: error.name || '',
    status_code: statusCode,
    api_path: apiPath,
    error: JSON.stringify(formatError2ErrorInfo(error)),
  };

  Object.entries(custom).forEach(([key, value]) => {
    log.setCustom(key, value);
  });

  log.send({ skip: { logbox: true } }).catch((e) => {
    console.error('[ ErrorFallback exposeLog Error ]', e);
  });
}

export function ErrorFallback({ error, resetErrorBoundary }: Props) {
  const hasSentLogRef = useRef(false);

  useEffect(() => {
    if (hasSentLogRef.current) return;
    hasSentLogRef.current = true;

    try {
      sendErrorExposeLog(error);
    } catch (e) {
      console.error('[ ErrorFallback sendErrorExposeLog failed ]', e);
    }
  }, [error]);

  const handleBackButtonClick = () => {
    TmapApp.onBackKeyPressed();
  };

  const handleReloadButtonClick = () => {
    resetErrorBoundary();
  };

  return (
    <div style={style.container}>
      <header style={style.header}>
        <button
          style={style.backButton}
          onClick={handleBackButtonClick}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M7.3572 13.4796L16.8072 4.08789L17.9586 5.2464L9.09141 14.0588L17.9586 22.8712L16.8072 24.0297L7.3572 14.6381C7.20295 14.4848 7.11621 14.2763 7.11621 14.0588C7.11621 13.8413 7.20295 13.6329 7.3572 13.4796Z"
              fill="#171819"
            />
          </svg>
        </button>
      </header>
      <div style={style.body}>
        <i style={style.icon}>
          <svg
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_4397_14400)">
              <path
                d="M62 32C62 48.5685 48.5685 62 32 62C15.4315 62 2 48.5685 2 32C2 15.4315 15.4315 2 32 2C48.5685 2 62 15.4315 62 32Z"
                fill="#EEF0F3"
              />
              <path
                d="M35.5667 18.3242C35.7677 16.2203 34.1134 14.4004 31.9999 14.4004C29.8864 14.4004 28.2321 16.2203 28.4331 18.3242L29.0182 24.4484L30.1864 36.5395C30.2498 37.1957 30.8012 37.6964 31.4605 37.6964H32.5393C33.1985 37.6964 33.75 37.1957 33.8134 36.5395L34.9816 24.4484L35.5667 18.3242ZM32 42.8255C30.2401 42.8255 28.8135 44.2708 28.8135 46.0307C28.8135 47.7906 30.2401 49.236 32 49.236C33.7599 49.236 35.1866 47.7906 35.1866 46.0307C35.1866 44.2708 33.7599 42.8255 32 42.8255Z"
                fill="#BEC5CC"
              />
            </g>
            <defs>
              <clipPath id="clip0_4397_14400">
                <rect
                  width="64"
                  height="64"
                  fill="white"
                />
              </clipPath>
            </defs>
          </svg>
        </i>
        <p style={style.message}>
          오류가 발생했어요.
          <br />
          다시 시도해 주세요.
        </p>
        <button
          style={style.reloadButton}
          onClick={handleReloadButtonClick}
        >
          새로고침
        </button>
      </div>
    </div>
  );
}

const style: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    height: 56,
    backgroundColor: '#FFFFFF',
    position: 'relative',
  },
  backButton: {
    width: 28,
    height: 28,
    background: 'none',
    border: 'none',
    padding: 0,
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
  },
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0 20px',
  },
  icon: {
    marginTop: 128,
  },
  message: {
    marginTop: 32,
    fontSize: '18px',
    lineHeight: '24px',
    fontWeight: 700,
    color: '#51565C',
    textAlign: 'center',
  },
  reloadButton: {
    marginTop: 24,
    padding: '8px 20px',
    fontSize: '16px',
    lineHeight: '24px',
    color: '#171819',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 0 0 1px inset #D1D5DB',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
  },
} as const;
